import { useState } from "react";
import { useLocation, useNavigate } from "@remix-run/react";
import { useTranslate } from "@made-people/grease-translations";
import {
  SelectorDefinition,
  buildSizeFacetId,
} from "./size-selector-categories";
import { ResolvedOverlayStyling } from "./size-selector-overlay-theme";
import { SizeSelectorOverlayShell } from "./size-selector-overlay-shell";

export type SizeSelectorOverlayDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  styling: ResolvedOverlayStyling;
  activeSizeChart: string;
  availableSelectors: SelectorDefinition[];
  optionsByKey: Record<string, string[]>;
  headline?: string;
  caption?: string;
  supportingText?: string;
};

export const SizeSelectorOverlayDialog = ({
  isOpen,
  onClose,
  styling,
  activeSizeChart,
  availableSelectors,
  optionsByKey,
  headline,
  caption,
  supportingText,
}: SizeSelectorOverlayDialogProps) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const location = useLocation();

  const [selection, setSelection] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | undefined>(undefined);

  const applyAndClose = () => {
    const missing = availableSelectors.find(
      (selector) => !selection[selector.key],
    );
    if (missing) {
      setError(translate(missing.errorKey));
      return;
    }
    setError(undefined);

    const params = new URLSearchParams(location.search);
    availableSelectors.forEach((selector) => {
      const facetId = buildSizeFacetId(selector.dimension, activeSizeChart);
      params.set(`facet_${facetId}`, selection[selector.key]);
    });

    navigate({ pathname: location.pathname, search: params.toString() });
    onClose();
  };

  return (
    <SizeSelectorOverlayShell
      isOpen={isOpen}
      onClose={onClose}
      styling={styling}
      headline={headline}
      caption={caption}
      supportingText={supportingText}
      onSubmit={applyAndClose}
      ctaDisabled={false}
      error={error}
    >
      <div className="mt-5 flex gap-3">
        {availableSelectors.map((selector) => (
          <label key={selector.key} className="flex-1">
            <span className="sr-only">
              {translate(selector.placeholderKey)}
            </span>
            <select
              value={selection[selector.key] ?? ""}
              onChange={(event) => {
                setSelection((prev) => ({
                  ...prev,
                  [selector.key]: event.target.value,
                }));
                setError(undefined);
              }}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-body-sm text-neutral-900"
            >
              <option value="" disabled>
                {translate(selector.placeholderKey)}
              </option>
              {(optionsByKey[selector.key] ?? []).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
    </SizeSelectorOverlayShell>
  );
};
